import { InfoIcon, InputMessage } from '@by-miles/bymiles-styles'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { randomString } from '../../utils/randomString'
import { ToolTipStyles } from './ToolTipStyles'

interface ByMilesToolTipProps {
  text: string | React.ReactNode
  className?: string
  dataCy?: string
  ariaLabel?: string
  tabFocusTrap?: number
}

export const ToolTip = ({
  text,
  className,
  dataCy = 'info-tool-tip',
  ariaLabel = 'More information',
  tabFocusTrap,
}: ByMilesToolTipProps) => {
  const infoButtonIdRef = useRef(randomString())
  const infoMessageIdRef = useRef(randomString())
  const container = useRef<HTMLHeadingElement>(null)
  const infoIcon = useRef<HTMLHeadingElement>(null)
  const banner = useRef<HTMLHeadingElement>(null)
  const [visible, setVisible] = useState(false)
  const [focused, setFocused] = useState<boolean>(false)
  const [position, setPosition] = useState({
    top: 0,
    bottom: 0,
    left: 0,
  })
  const [bannerSize, setBannerSize] = useState({
    height: 0,
    width: 0,
  })

  const calculatePositions = () => {
    const infoIconPosition = infoIcon?.current?.getBoundingClientRect()
    if (infoIconPosition) {
      setPosition(infoIconPosition)
    }
    const bannerProps = document
      .getElementById(infoMessageIdRef.current)
      ?.getElementsByClassName('info-modal-message')[0]
      ?.getBoundingClientRect()
    if (bannerProps) {
      const outOfBounds = bannerProps.right > (window.innerWidth || document.documentElement.clientWidth)
      const bannerClass = banner?.current?.classList
      const previouslyOutOfBounds = bannerClass?.contains('out-of-bounds')
      if (outOfBounds && !previouslyOutOfBounds) {
        bannerClass?.add('out-of-bounds')
      }
      if (!outOfBounds && previouslyOutOfBounds) {
        bannerClass?.remove('out-of-bounds')
      }
      setBannerSize({
        height: bannerProps.height,
        width: bannerProps.width,
      })
    }
  }

  function handleClickOutside(event: any) {
    if (container?.current && !container?.current.contains(event.target)) {
      setVisible(false)
    }
  }

  useEffect(() => {
    const hideOnResize = () => setVisible(false)
    window.addEventListener('resize', hideOnResize)
    document.addEventListener('click', event => handleClickOutside(event))
  }, [])

  useLayoutEffect(() => {
    if (visible) {
      calculatePositions()
    }
  }, [visible])

  return (
    <ToolTipStyles
      focused={focused}
      position={position}
      bannerHeight={bannerSize.height}
      bannerWidth={bannerSize.width}
      ref={container}
      className={className}
      visible={visible}
    >
      <div id={infoButtonIdRef.current} className="info-icon" onClick={() => setVisible(!visible)} ref={infoIcon}>
        <InfoIcon />
      </div>
      <button
        type="button"
        className="info-icon-hidden"
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false)
          setVisible(false)
        }}
        onClick={() => setVisible(!visible)}
        aria-label={ariaLabel}
        aria-labelledby={infoButtonIdRef.current}
        aria-expanded={visible}
        aria-controls={infoMessageIdRef.current}
        data-cy={dataCy}
        tabIndex={tabFocusTrap}
      />
      <div id={infoMessageIdRef.current} className="banner-wrapper" onClick={() => setVisible(!visible)} ref={banner}>
        <InputMessage position="top" type="info" message={text} className="info-modal-message" />
      </div>
    </ToolTipStyles>
  )
}
